import { useCallback, useMemo, useState } from "react";
import { WpGenericPost, WpTicketMetaBox } from "./types";
import { addQueryArgs } from "@wordpress/url";
import { TicketTableFilter } from "@hooks/use-table-context/ticket/types";
import { useMultiSiteContext } from "../../use-site-context";
import { useScreenAlert } from "../../use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import {
  getApiOperator,
  buildDateRangeQueries,
  sanitizeInput,
} from "../utils";

const TICKET_API_URL: string = "wp-json/wp/v2/ticket";
const WP_TICKET_NUMBER_ENDPOINT: string = `${TICKET_API_URL}/?search=`;

export const useGetTickets = () => {
  const [state, setState] = useState<{
    items: WpGenericPost<WpTicketMetaBox>[];
    total: number;
  }>({
    items: [],
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const { getApiUrl } = useMultiSiteContext();

  const ticketUrl = useMemo(() => getApiUrl(TICKET_API_URL), [getApiUrl]);
  const ticketNumberUrl = useMemo(
    () => getApiUrl(WP_TICKET_NUMBER_ENDPOINT),
    [getApiUrl],
  );

  const getOneTicket = useCallback(
    async (id: number) => {
      setLoading(true);

      try {
        const ticket = await fetchWithTokenRefresh(
          addQueryArgs(`${ticketUrl}/${id}`, {}),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        ).then((res) => {
          if (!res.ok) {
            throw new Error("Network response was not ok");
          }
          setLoading(false);
          return res.json();
        });

        return ticket;
      } catch (err) {
        setError(
          "An error occurred while fetching the ticket. Please try again.",
        );
        setLoading(false);
        return null;
      }
    },
    [ticketUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  const getOneTicketByTicketNumber = useCallback(
    async (ticketNumber: string) => {
      setLoading(true);

      try {
        const ticket = await fetchWithTokenRefresh(
          addQueryArgs(`${ticketNumberUrl}${ticketNumber}`, {}),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        ).then((res) => {
          if (!res.ok) {
            throw new Error("Network response was not ok");
          }
          setLoading(false);
          return res.json();
        });
        return ticket[0];
      } catch (err) {
        setError("Error fetching ticket: " + (err as Error)?.message);
        setLoading(false);
        return null;
      }
    },
    [ticketNumberUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  const getTickets = useCallback(
    async ({
      filters,
      searchValue,
    }: {
      filters?: TicketTableFilter[];
      searchValue?: string;
    }) => {
      setLoading(true);
      try {
        const baseFilterQueries =
          filters?.reduce?.(
            (acc, filter) => {
              const { column, value, type, endValue } = filter;

              // Pass WP core pagination params directly — no _compare suffix
              if (
                (column as string) === "per_page" ||
                (column as string) === "page"
              ) {
                acc[column as string] = String(value ?? "");
                return acc;
              }

              // Handle date range filters
              if (filter.inputType && filter.inputType === "date") {
                const rangeQueries = buildDateRangeQueries(
                  column as string,
                  value?.toString() || null,
                  endValue?.toString() || null,
                );
                return { ...acc, ...rangeQueries };
              }

              // Handle regular filters - sanitize values
              const sanitizedValue = sanitizeInput(value || "");
              acc[column as string] = sanitizedValue;
              acc[column + "_compare"] = getApiOperator(type);
              return acc;
            },
            {} as Record<string, string>,
          ) ?? {};

        let filterQueries: Record<string, string> = baseFilterQueries;

        if (searchValue) {
          filterQueries = {
            ...(baseFilterQueries.page ? { page: baseFilterQueries.page } : {}),
            ...(baseFilterQueries.per_page
              ? { per_page: baseFilterQueries.per_page }
              : {}),
            search: sanitizeInput(searchValue),
            search_columns: "post_title",
          };
        }
        let total = 0;
        const data = await fetchWithTokenRefresh(
          addQueryArgs(ticketUrl, filterQueries),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        )
          .then((response) => {
            if (!response.ok) {
              if (response.status === 403) {
                throw new Error("You do not have permission to view tickets.");
              }
              throw new Error(`Failed to fetch tickets: ${response.status}`);
            }
            total = parseInt(response.headers.get("X-WP-Total") || "0");
            return response.json();
          })
          .then((data) => {
            return {
              items: data,
              total,
            };
          });

        setState({ items: data.items, total: data.total } as any);
        setLoading(false);
        return { items: data.items, total: data.total };
      } catch (error) {
        setError("An error occurred while fetching tickets. Please try again.");
        setLoading(false);
        return { items: [], total: 0 };
      }
    },
    [ticketUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  return {
    tickets: state.items,
    total: state.total,
    loading,
    getTickets,
    getOneTicket,
    getOneTicketByTicketNumber,
  };
};
